import { formatInReal } from '../../../../commons/util/HallAppUtil';

function AdSummary({ values, imageFile, adAccounts, pages }) {
    const calculateDuration = () => {
        if (!values.startDate || !values.endDate) return 0;
        const start = new Date(values.startDate);
        const end = new Date(values.endDate);
        const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        return diff > 0 ? diff : 0;
    };

    const calculateTotalBudget = () => {
        const days = calculateDuration();
        return (values.dailyBudget / 100) * days;
    };

    const getAdAccountName = () => {
        const account = adAccounts.find(acc => acc.id === values.adAccountId);
        return account?.name || values.adAccountId;
    };

    const getPageName = () => {
        const page = pages.find(p => p.id === values.pageId);
        return page?.name || values.pageId;
    };

    const getObjectiveName = () => {
        const objectives = {
            'LINK_CLICKS': 'Tráfego (cliques no link)',
            'REACH': 'Alcance',
            'BRAND_AWARENESS': 'Reconhecimento da marca',
        };
        return objectives[values.objective] || values.objective;
    };

    const getGenderName = () => {
        const genders = {
            'male': '👨 Homens',
            'female': '👩 Mulheres',
            'all': '👥 Todos (Homens e Mulheres)'
        };
        return genders[values.targetGender] || '👥 Todos';
    };

    const getLinkType = () => {
        if (!values.destinationUrl) return '❌ Não configurado';
        if (values.destinationUrl.includes('wa.me')) {
            return '💬 WhatsApp';
        }
        return '🔗 Link Personalizado';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatAudience = (size) => {
        if (!size && size !== 0) return '';
        if (typeof size === 'number') return ` • ${Number(size).toLocaleString()} pessoas`;
        return ` • ${size}`;
    };

    const renderLocations = () => {
        const locs = Array.isArray(values.locations) ? values.locations : [];
        if (locs.length === 0) return <span className="summary-value">Não definida</span>;

        return (
            <div className="summary-list">
                {locs.map((l, i) => (
                    <div key={l.primary_city_id ?? l.name ?? i} className="summary-chip">
                        <span className="chip-icon">📍</span>
                        <div className="chip-text">
                            <div className="chip-title">{l.name}{l.region ? `, ${l.region}` : ''}</div>
                            <div className="chip-sub">{l.country_code}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderInterests = () => {
        const ints = Array.isArray(values.interests) ? values.interests : [];
        if (ints.length === 0) return <span className="summary-value">Não selecionados</span>;

        return (
            <div className="summary-list">
                {ints.map((it, i) => (
                    <div key={it.id ?? it.name ?? i} className="summary-chip interest-chip">
                        <span className="chip-icon">🎯</span>
                        <div className="chip-text">
                            <div className="chip-title">{it.name}</div>
                            <div className="chip-sub">{it.audience_size ? `${Number(it.audience_size).toLocaleString()} pessoas` : 'Alvo genérico'}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="ad-summary-card">
            <h4 className="summary-title">📊 Resumo da Campanha</h4>

            <div className="summary-section">
                <div className="summary-section-title">🏢 Conta e Página</div>
                <div className="summary-item">
                    <span className="summary-label">Conta de Anúncios:</span>
                    <span className="summary-value">{getAdAccountName()}</span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Página:</span>
                    <span className="summary-value">{getPageName()}</span>
                </div>
            </div>

            <div className="summary-section">
                <div className="summary-section-title">💰 Orçamento e Período</div>
                <div className="summary-item">
                    <span className="summary-label">Objetivo:</span>
                    <span className="summary-value">{getObjectiveName()}</span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Orçamento Diário:</span>
                    <span className="summary-value highlight-budget">{formatInReal(values.dailyBudget)}</span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Duração:</span>
                    <span className="summary-value">{calculateDuration()} dias</span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Orçamento Total Estimado:</span>
                    <span className="summary-value highlight-total">{formatInReal(calculateTotalBudget() * 100)}</span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Início:</span>
                    <span className="summary-value">{formatDate(values.startDate)}</span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Término:</span>
                    <span className="summary-value">{formatDate(values.endDate)}</span>
                </div>
            </div>

            <div className="summary-section">
                <div className="summary-section-title">🎯 Público-Alvo</div>
                <div className="summary-item">
                    <span className="summary-label">Localização:</span>
                    <div className="summary-value summary-value-block">
                        {renderLocations()}
                    </div>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Faixa Etária:</span>
                    <span className="summary-value">{values.ageMin || 18} - {values.ageMax || 65} anos</span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Gênero:</span>
                    <span className="summary-value">{getGenderName()}</span>
                </div>

                <div className="summary-item" style={{ marginTop: 12 }}>
                    <span className="summary-label">Interesses:</span>
                    <div className="summary-value summary-value-block">
                        {renderInterests()}
                    </div>
                </div>
            </div>

            <div className="summary-section">
                <div className="summary-section-title">🎨 Criativo do Anúncio</div>
                <div className="summary-item">
                    <span className="summary-label">Título (Headline):</span>
                    <span className="summary-value">{values.headline || 'Não definido'}</span>
                </div>
                <div className="summary-item full-width">
                    <span className="summary-label">Texto Principal:</span>
                    <span className="summary-value link-value">
                        {values.primaryText ? 
                            (values.primaryText.length > 100 ? 
                                `${values.primaryText.substring(0, 100)}...` : 
                                values.primaryText
                            ) : 
                            'Não definido'
                        }
                    </span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Tipo de Link:</span>
                    <span className="summary-value">{getLinkType()}</span>
                </div>
                <div className="summary-item full-width">
                    <span className="summary-label">Link de Destino:</span>
                    <span className="summary-value link-value">
                        {values.destinationUrl || 'Não configurado'}
                    </span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Imagem:</span>
                    <span className="summary-value">
                        {imageFile ? '✅ Selecionada' : '❌ Não selecionada'}
                    </span>
                </div>
            </div>

            {imageFile && (
                <div className="summary-section">
                    <div className="summary-section-title">🖼️ Preview da Imagem</div>
                    <div className="summary-image-preview">
                        <img src={URL.createObjectURL(imageFile)} alt="Preview do anúncio" />
                    </div>
                </div>
            )}

            <div className="summary-footer">
                <p style={{ 
                    fontSize: '13px', 
                    color: '#5a6c7d', 
                    margin: 0,
                    textAlign: 'center',
                    padding: '12px',
                    background: '#f8f9fa',
                    borderRadius: '8px'
                }}>
                    ℹ️ Revise todas as informações antes de criar o anúncio. Após a criação, algumas configurações não poderão ser alteradas.
                </p>
            </div>
        </div>
    );
}

export default AdSummary;